import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { PageShell } from "./PageShell";
import { BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in all fields.");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — we'll be in touch soon. 🌿");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/40 outline-none transition-colors duration-300 focus:border-sage focus:ring-2 focus:ring-sage/20";

  return (
    <PageShell title="Contact Us" intro="Have a question, an idea, or just want to say hello? We'd love to hear from you.">
      <p className="flex items-center gap-2 text-sm text-ink/70">
        <Mail size={16} className="text-sage" /> Prefer email? Reach us at{" "}
        <a className="text-sage-deep underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>
      </p>

      {sent ? (
        <div className="rounded-3xl border border-line bg-white p-8 text-center shadow-[0_8px_26px_rgba(47,75,57,0.05)]" data-testid="contact-success">
          <h2 className="font-serif text-2xl font-semibold text-heading">Thank you.</h2>
          <p className="mt-2 text-sm text-ink/70">Your message is on its way to our team. We read every note with care.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-3xl border border-line bg-white p-6 sm:p-8 shadow-[0_8px_26px_rgba(47,75,57,0.05)] space-y-4" data-testid="contact-form">
          <input name="name" value={form.name} onChange={onChange} placeholder="Your name" className={inputCls} data-testid="contact-name" />
          <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Your email" className={inputCls} data-testid="contact-email" />
          <textarea name="message" value={form.message} onChange={onChange} placeholder="How can we help?" rows={5} className={inputCls} data-testid="contact-message" />
          <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-full bg-sage px-6 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-sage-hover disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send Message"}
          </button>
        </form>
      )}
    </PageShell>
  );
}
